import { ChangeDetectionStrategy, Component } from '@angular/core';
import { DashboardItemSettingsComponent } from '../dashboard-item-settings.component';
import { DashboardItemDataGraph } from '../../models/DashboardItemDataGraph.model';
import { DataDisplayDefaultRanges } from '../../../../shared/models/DataDisplayDefaultRanges';
import { of } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { GraphSettingsSeriesComponent } from './graph-settings-series/graph-settings-series.component';
import { EmuListInputComponent } from '../../../../shared/components/inputs/emu-list-input/emu-list-input.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';

import { DashboardItemDataGraphTypesModel } from '../../models/DashboardItemDataGraphTypes.model';
import { EmuInputComponent } from '../../../../shared/components/inputs/emu-input/emu-input.component';
import { getDefaultSeries } from './graph-settings-series/getDefaultSeries';
import { maxSeriesForGraphType } from './graph-settings-series/maxSeriesForGraphType';
import { getAvailableRangesForGraphType } from './getAvailableRangesForGraphType';
import { SankeyChartConfigComponent } from './sankey-chart-config/sankey-chart-config.component';
import { SkeletonComponent } from '../../../../shared/components/skeleton/skeleton.component';
import { UplotRange } from '../../../../shared/components/graph-uplot/graph/models/UplotRange.model';
import { RangePickerComponent } from '../../../../shared/range-picker/range-picker.component';
import { ScaleSettingsComponent } from '../../../../shared/components/graph-uplot/graph/scale-settings/scale-settings.component';
import { getRangeFromDataDisplayDefaultRanges } from '../../../../shared/utils/getRangeFromDataDisplayDefaultRanges';
import { isIntervalAvailableForRange } from '../../../../shared/components/graph-uplot/graph/config/uplotScales';
import { DataRequestDto } from '../../../../../api-main';

@Component({
  selector: 'app-dashboard-graph-settings',
  templateUrl: './dashboard-graph-settings.component.html',
  styleUrls: ['../../styles/widget-settings.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    MatCheckboxModule,
    ReactiveFormsModule,
    FormsModule,
    EmuListInputComponent,
    GraphSettingsSeriesComponent,
    TranslateModule,
    EmuInputComponent,
    SankeyChartConfigComponent,
    SkeletonComponent,
    RangePickerComponent,
    ScaleSettingsComponent,
  ],
})
export class DashboardGraphSettingsComponent extends DashboardItemSettingsComponent {
  //TODO here: specific scales can be selected based on range, but after selecting scale we can change range.
  // there should be verification after changing range and if scale is not valid for new range we should change it

  getRangeInHours() {
    if (!this.data) return null;
    if (this.data.range && this.data.userDefinedRange == DataDisplayDefaultRanges.CUSTOM) {
      return (this.data.range.max - this.data.range.min) / 3600;
    } else {
      const range = getRangeFromDataDisplayDefaultRanges(this.data.userDefinedRange);
      return (range.max - range.min) / 3600;
    }
  }

  getDefinedRanges(data: DashboardItemDataGraph = this.data) {
    return [DataDisplayDefaultRanges.CUSTOM, ...getAvailableRangesForGraphType(data)];
  }

  isPiechart() {
    return [
      DashboardItemDataGraphTypesModel.PIE_MONTHLY_AGG,
      DashboardItemDataGraphTypesModel.PIE_WEEKLY_AGG,
      DashboardItemDataGraphTypesModel.PIE,
    ].includes(this.data.type);
  }

  isHeatmap() {
    return [
      DashboardItemDataGraphTypesModel.HEATMAP_DAILY,
      DashboardItemDataGraphTypesModel.HEATMAP,
    ].includes(this.data.type);
  }

  isSankey() {
    return [DashboardItemDataGraphTypesModel.SANKEY].includes(this.data.type);
  }

  isColorConfigurable() {
    return [
      DashboardItemDataGraphTypesModel.DEFAULT,
      DashboardItemDataGraphTypesModel.PIE,
    ].includes(this.data.type);
  }

  isDefaultChart() {
    return [DashboardItemDataGraphTypesModel.DEFAULT].includes(this.data.type);
  }

  forceConsumption() {
    return this.isPiechart() ? true : null;
  }

  showConsumption() {
    return !this.isPiechart();
  }

  public graphTypes = Object.keys(DashboardItemDataGraphTypesModel)
    .filter((o) => !isNaN(Number(o)))
    .map((o) => +o);

  override get data(): DashboardItemDataGraph {
    const data = this.itemData?.graph;
    this.dataValidator(data);

    return data;
  }

  private dataValidator(data: DashboardItemDataGraph) {
    if (!data) return;
    const maxSeriesLength = maxSeriesForGraphType(data.type);
    if (data.series.length > maxSeriesLength) {
      data.series.length = maxSeriesLength;
      // this.cd.detectChanges();
    }
    const availableRanges = this.getDefinedRanges(data);
    if (!availableRanges.includes(data.userDefinedRange) && availableRanges.length > 0) {
      // console.log('=>(dashboard-graph-settings.component.ts:104) availableRanges', availableRanges);
      data.userDefinedRange = availableRanges.filter(
        (o) => o != DataDisplayDefaultRanges.CUSTOM,
      )[0];
      // this.cd.detectChanges();
    }
  }

  protected override setDefault() {
    if (this.data == undefined)
      this.itemData.graph = {
        ready: false,
        showSettings: true,
        showRange: false,
        userDefinedRange: DataDisplayDefaultRanges.CURRENT_DAY,
        type: DashboardItemDataGraphTypesModel.DEFAULT,
        series: [getDefaultSeries()],
      };
    this.graphReady();
  }

  getRange(): UplotRange {
    return this.data.range;
  }

  changeRange(range: UplotRange) {
    this.rangeHasChanged();
    this.data.range = range;
    this.reloadGraph();
  }

  rangeHasChanged() {
    if (!isIntervalAvailableForRange(this.data.scale, this.getRangeInHours())) {
      this.data.scale = DataRequestDto.ScaleEnum.AUTO;
    }
  }

  graphReady() {
    this.itemData.graph.ready = true;
  }

  reloadGraph() {
    this.data.refresh = true;
  }

  refreshSeries() {
    if (this.data.name === '') this.data.name = undefined;
    this.data.seriesChanges = true;
  }

  public getRangeString(scale: string): string {
    // return `UPLOT.RANGE.${DataDisplayDefaultRanges[Number(scale)].toUpperCase()}`;
    return `UPLOT.RANGE.${scale.toUpperCase()}`;
  }

  public getTypeString(scale: number): string {
    return `DASHBOARD.GRAPH_TYPES.${DashboardItemDataGraphTypesModel[Number(scale)].toUpperCase()}`;
  }

  protected readonly of = of;

  protected readonly DataDisplayDefaultRanges = DataDisplayDefaultRanges;
}
